import { useMutation } from '@tanstack/react-query'
import Button from 'ext-ui/components/Button/Button'
import TextAreaInput, {
  TextAreaInputRef,
} from 'ext-ui/components/TextAreaInput/TextAreaInput'
import React, {
  FormEvent,
  KeyboardEvent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslate } from 'src/localization/Trns'
import { TRANSACTION_COMMENT_REQUEST_TIMEOUT_MS } from 'src/queries/transactionConversations/transactionConversations.network'
import { transactionConversationMutations } from 'src/queries/transactionConversations/transactionConversations.queries'

import * as style from './TransactionCommentComposer.style'

interface TransactionCommentComposerProps {
  transactionId: string
  onPosted: (messageId: string) => void
}

const TransactionCommentComposer = ({
  transactionId,
  onPosted,
}: TransactionCommentComposerProps): ReactElement => {
  const translate = useTranslate()
  const { mutateAsync: postMessage } = useMutation(
    transactionConversationMutations.postMessage(),
  )
  const triggerRef = useRef<HTMLButtonElement>(null)
  const textareaRef = useRef<TextAreaInputRef>(null)
  const restoreTriggerFocusRef = useRef(false)
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [idempotencyKey, setIdempotencyKey] = useState<string | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [isSending, setIsSending] = useState(false)
  const [posted, setPosted] = useState(false)

  useEffect(() => {
    if (isOpen) {
      textareaRef.current?.focus()
      return
    }

    if (restoreTriggerFocusRef.current) {
      restoreTriggerFocusRef.current = false
      triggerRef.current?.focus()
    }
  }, [isOpen])

  const discardDraft = (restoreTriggerFocus: boolean): void => {
    if (isSending) return

    restoreTriggerFocusRef.current = restoreTriggerFocus
    setDraft('')
    setError(undefined)
    setIdempotencyKey(undefined)
    setPosted(false)
    setIsOpen(false)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Escape' || isSending) return

    event.preventDefault()
    discardDraft(true)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSending || !draft.trim()) return

    const nextIdempotencyKey = idempotencyKey ?? crypto.randomUUID()
    if (!idempotencyKey) {
      setIdempotencyKey(nextIdempotencyKey)
    }

    setError(undefined)
    setPosted(false)
    setIsSending(true)

    const requestController = new AbortController()
    const timeoutId = window.setTimeout(
      () => requestController.abort(),
      TRANSACTION_COMMENT_REQUEST_TIMEOUT_MS,
    )
    const rejectOnAbort = new Promise<never>((_, reject) => {
      requestController.signal.addEventListener(
        'abort',
        () => reject(new Error('Transaction comment request timed out')),
        { once: true },
      )
    })

    try {
      const response = await Promise.race([
        postMessage({
          transactionId,
          body: draft,
          idempotencyKey: nextIdempotencyKey,
          signal: requestController.signal,
        }),
        rejectOnAbort,
      ])
      setDraft('')
      setIdempotencyKey(undefined)
      setPosted(true)
      onPosted(response.id)
    } catch {
      setError(translate('Something went wrong. Please try again.'))
    } finally {
      window.clearTimeout(timeoutId)
      setIsSending(false)
    }
  }

  if (!isOpen) {
    return (
      <Button ref={triggerRef} variant="text" onClick={() => setIsOpen(true)}>
        {translate('+ Add Comment')}
      </Button>
    )
  }

  const errorId = 'transaction-comment-error'

  return (
    <form css={style.composer} onSubmit={handleSubmit}>
      <TextAreaInput
        ref={textareaRef}
        label={translate('Comment')}
        autoResize
        value={draft}
        disabled={isSending}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={Boolean(error)}
        onChange={(event) => {
          const nextDraft = event.target.value
          if (error && nextDraft !== draft) {
            setIdempotencyKey(undefined)
          }
          setDraft(nextDraft)
          setError(undefined)
          setPosted(false)
        }}
        onKeyDown={handleKeyDown}
      />
      {error && (
        <p id={errorId} css={style.error} role="alert">
          {error}
        </p>
      )}
      <p role="status" aria-atomic="true">
        {posted ? translate('Comment posted.') : ''}
      </p>
      <div css={style.actions}>
        <Button
          type="button"
          variant="secondary"
          disabled={isSending}
          onClick={() => discardDraft(true)}
        >
          {translate('Cancel')}
        </Button>
        <Button type="submit" loading={isSending} disabled={!draft.trim()}>
          {translate(isSending ? 'Sending…' : 'Send')}
        </Button>
      </div>
    </form>
  )
}

export default TransactionCommentComposer
